import { useCallback, useEffect, useMemo, useState } from "react";
import {
  isDefaultColumnState,
  sanitizeColumnState,
  tableTemplatesOf,
  useSettings,
  type ColumnState,
  type TableDef,
  type TableId,
  type TableTemplatesDoc,
} from "@helm/core";
import { settingsSync } from "../session";

/**
 * Column state of one table instance. With `onChange` (a docked panel) the state lives in the
 * panel's params and is saved with the workspace layout; without it (a table not in a panel) it
 * is kept in component state. Default state is stored as `null`, so an untouched table follows
 * future default changes.
 */
export function useColumnState<R>(
  def: TableDef<R>,
  raw: unknown,
  onChange: ((s: ColumnState | null) => void) | undefined,
): [ColumnState, (s: ColumnState) => void] {
  const [local, setLocal] = useState<ColumnState | null>(null);
  const source = onChange ? raw : local;
  const state = useMemo(() => sanitizeColumnState(source, def), [source, def]);
  const set = useCallback(
    (s: ColumnState) => {
      const v =
        isDefaultColumnState(s, def) && (s.template === null || s.template === (def.defaultTemplate ?? null))
          ? null
          : s;
      if (onChange) onChange(v);
      else setLocal(v);
    },
    [def, onChange],
  );
  return [state, set];
}

/** Panel params value: every table's column state in one map (`WidgetParams.tables`). */
export type TablesParam = Partial<Record<TableId, ColumnState>>;

/** Read one table's raw state from the params map. */
export function tableParamOf(tables: unknown, id: TableId): unknown {
  return typeof tables === "object" && tables !== null ? (tables as Record<string, unknown>)[id] : undefined;
}

/** Write one table's state into the params map (null removes it). */
export function withTableParam(tables: unknown, id: TableId, s: ColumnState | null): TablesParam {
  const base = typeof tables === "object" && tables !== null ? (tables as TablesParam) : {};
  const others = Object.entries(base).filter(([k]) => k !== id);
  return Object.fromEntries(s === null ? others : [...others, [id, s]]);
}

/** User-level table templates from the synced settings document, and a saver. */
export function useTableTemplates(): {
  doc: TableTemplatesDoc;
  save: (next: TableTemplatesDoc) => void;
} {
  const raw = useSettings((s) => s.settings.table_templates);
  const doc = useMemo(() => tableTemplatesOf(raw), [raw]);
  const save = useCallback((next: TableTemplatesDoc) => {
    void settingsSync.update({ table_templates: next });
  }, []);
  return { doc, save };
}

/** Epoch ms, refreshed every second while `active` (Duration columns). */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(t);
    };
  }, [active]);
  return now;
}
